// Field use (guide 19.7): a status set on a tablet with no signal is kept on
// the device and sent when the connection comes back, in the order it was
// recorded and with the time it was recorded (the server keeps that time if
// it is within seven days). Kept in localStorage per project: it survives a
// reload, and the queue is small (one entry per status action, not per row).
//
//   sendOrQueue(projectId, viewId, path, body)  -> { queued: true } or the server's answer
//   flush(projectId)                            -> { sent, rejected, left }
//   pending(projectId, viewId)                  -> the queued entries
//   pendingStates(projectId, viewId)            -> Map rowId -> status (what the screen shows until sent)
import { viewsApi } from "./viewModel";

const KEY = (projectId) => `bl_field_outbox_${projectId}`;
const OFFLINE_PATHS = ["status", "blocked", "inspected"];

function read(projectId) {
  try { const v = JSON.parse(localStorage.getItem(KEY(projectId)) || "[]"); return Array.isArray(v) ? v : []; } catch (e) { return []; }
}
function write(projectId, items) {
  try {
    if (items.length) localStorage.setItem(KEY(projectId), JSON.stringify(items));
    else localStorage.removeItem(KEY(projectId));
  } catch (e) { /* storage full or blocked: the change stays unsent, and says so */ }
  try { window.dispatchEvent(new CustomEvent("bl-outbox", { detail: { projectId } })); } catch (e) { /* old browsers */ }
}

// A failure of the network, not an answer from the server.
export const isNetworkError = (e) => !!e && (e.name === "TypeError" || /failed to fetch|network|load failed/i.test(String(e.message || "")));
const offline = () => typeof navigator !== "undefined" && navigator.onLine === false;

export function pending(projectId, viewId) {
  return read(projectId).filter((i) => !viewId || i.viewId === viewId);
}

export function pendingStates(projectId, viewId) {
  const out = new Map();
  pending(projectId, viewId).forEach((i) => {
    if (i.path === "status" && i.body && i.body.status) (i.body.rowIds || []).forEach((id) => out.set(String(id), i.body.status));
  });
  return out;
}

export async function sendOrQueue(projectId, viewId, path, body) {
  const queue = () => {
    const items = read(projectId);
    items.push({ id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, viewId, path, body, recordedAt: new Date().toISOString() });
    write(projectId, items);
    return { queued: true };
  };
  if (OFFLINE_PATHS.includes(path) && (offline() || read(projectId).length)) return queue();   // keep the order: nothing overtakes the queue
  try {
    return await viewsApi(projectId, `/${viewId}/tracking/${path}`, { method: "POST", body });
  } catch (e) {
    if (OFFLINE_PATHS.includes(path) && isNetworkError(e)) return queue();
    throw e;
  }
}

let flushing = null;
export function flush(projectId) {
  if (flushing) return flushing;
  flushing = (async () => {
    let sent = 0; const rejected = [];
    let items = read(projectId);
    while (items.length) {
      const it = items[0];
      try {
        // eslint-disable-next-line no-await-in-loop
        await viewsApi(projectId, `/${it.viewId}/tracking/${it.path}`, { method: "POST", body: { ...it.body, recordedAt: it.recordedAt } });
        sent += 1;
      } catch (e) {
        if (isNetworkError(e)) break;                     // still offline: try again later, in order
        rejected.push({ ...it, error: e.message || "refused" });   // the server said no: do not retry forever
      }
      items = read(projectId).filter((x) => x.id !== it.id);
      write(projectId, items);
    }
    return { sent, rejected, left: read(projectId).length };
  })().finally(() => { flushing = null; });
  return flushing;
}
